import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, createBlock as _createBlock, normalizeClass as _normalizeClass, mergeProps as _mergeProps, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "wage-diag-header" }
const _hoisted_2 = { class: "wage-diag-header__icon" }
const _hoisted_3 = { class: "wage-diag-header__text" }
const _hoisted_4 = {
  key: 0,
  class: "wage-diag-header__sub"
}
const _hoisted_5 = { class: "wage-diag-form" }
const _hoisted_6 = { class: "wage-diag-check" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = { class: "wage-diag-actions" }
const _hoisted_9 = {
  key: 1,
  class: "wage-diag-file-name"
}
const _hoisted_10 = { class: "wage-diag-preview" }
const _hoisted_11 = { class: "wage-diag-preview__bar" }
const _hoisted_12 = { class: "wage-diag-preview__tabs" }
const _hoisted_13 = { class: "wage-diag-preview__title-wrap" }
const _hoisted_14 = { class: "wage-diag-preview__label" }
const _hoisted_15 = {
  key: 0,
  class: "wage-diag-preview__filename"
}
const _hoisted_16 = { class: "wage-diag-preview__actions" }
const _hoisted_17 = { class: "wage-diag-preview__body" }
const _hoisted_18 = ["src", "title"]
const _hoisted_19 = {
  key: 1,
  class: "wage-diag-preview__empty"
}
const _hoisted_20 = {
  key: 2,
  class: "wage-diag-preview__empty"
}

import { ref, computed, watch, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import * as XLSX from 'xlsx';
import { crawlApi } from '@/services/api';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import DeleteConfirmDialog from '@/components/common/DeleteConfirmDialog.vue';

interface WageRow {
  name: string;
  worker: string;
  time?: number;
  base?: number;
  overtime?: number;
  night?: number;
  holiday?: number;
  longTerm?: number;
  sunim?: number;
  dang?: number;
  position?: number;
  etc?: number;
  lunch?: number;
  car?: number;
  childcare?: number;
  l_yeon?: number;
  gun?: number;
  jang?: number;
  go?: number;
  boss?: boolean;
}

interface FacilityTarget {
  ccNumber: string;
  name: string;
  biz?: string;
  wageDiagCount?: number;
  wageDiagWdate?: string;
  wageDiagUId?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'WageSimpleDiagnosisDialog',
  props: {
    modelValue: { type: Boolean },
    facility: {}
  },
  emits: ["update:modelValue", "saved", "deleted"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const store = useStore();

const open = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
});

/** 최고관리자(SUPER_ADMIN, role_level 100) 전용 */
const isSuperAdmin = computed(
  () => Number(store.getters.actualRoleLevel ?? store.getters.roleLevel ?? 0) >= 100,
);

const loading = ref(false);
const deleting = ref(false);
const showDeleteConfirm = ref(false);
const loadingText = ref('처리 중...');
const error = ref('');
const ccCode = ref('');
const name = ref('');
const biz = ref('');
const ym = ref('');
const nonTaxLunch = ref(true);
const rows = ref<WageRow[]>([]);
const fileName = ref('');
const fileRef = ref<HTMLInputElement | null>(null);
const pdfUrl = ref<string | null>(null);
const pdfBlob = ref<Blob | null>(null);
const pdfFileName = ref('');
const pdfUrlV2 = ref<string | null>(null);
const pdfBlobV2 = ref<Blob | null>(null);
const pdfFileNameV2 = ref('');
const reportVersion = ref<'v1' | 'v2'>('v1');
const savedInfo = ref<FacilityTarget | null>(null);

const rowCount = computed(() => rows.value.length);
const hasSaved = computed(() => (savedInfo.value?.wageDiagCount || 0) > 0);
const hasReportPreview = computed(() => !!pdfUrl.value || !!pdfUrlV2.value);
const activePdfUrl = computed(() => (reportVersion.value === 'v1' ? pdfUrl.value : pdfUrlV2.value));
const activePdfBlob = computed(() => (reportVersion.value === 'v1' ? pdfBlob.value : pdfBlobV2.value));
const activePdfFileName = computed(() => (
  reportVersion.value === 'v1' ? pdfFileName.value : pdfFileNameV2.value
));

const num = (v: unknown) => {
  if (v == null || v === '') return 0;
  const n = parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const sanitizeFilePart = (value: string) => value
  .replace(/[\\/:*?"<>|]/g, '_')
  .replace(/\s+/g, '')
  .trim();

const buildPdfFileName = () => {
  const facility = sanitizeFilePart(name.value) || '시설';
  const code = sanitizeFilePart(ccCode.value) || '코드없음';
  const month = ym.value || new Date().toISOString().slice(0, 7);
  return `임금대장_개선결과보고서_${facility}_${code}_${month}.pdf`;
};

const buildPdfFileNameV2 = () => {
  const facility = sanitizeFilePart(name.value) || '시설';
  const code = sanitizeFilePart(ccCode.value) || '코드없음';
  const month = ym.value || new Date().toISOString().slice(0, 7);
  return `임금대장_무료진단보고서_${facility}_${code}_${month}.pdf`;
};

const initYm = () => {
  const now = new Date();
  ym.value = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const revokePdfUrl = (url: string | null) => {
  if (!url) return;
  const base = url.split('#')[0];
  window.URL.revokeObjectURL(base);
};

const resetPdf = () => {
  revokePdfUrl(pdfUrl.value);
  revokePdfUrl(pdfUrlV2.value);
  pdfUrl.value = null;
  pdfBlob.value = null;
  pdfFileName.value = '';
  pdfUrlV2.value = null;
  pdfBlobV2.value = null;
  pdfFileNameV2.value = '';
};

const applyPdfBase64 = (base64: string, fileName: string | undefined, version: 'v1' | 'v2') => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const resolvedName = fileName || (version === 'v1' ? buildPdfFileName() : buildPdfFileNameV2());
  const url = window.URL.createObjectURL(blob);
  const pdfSrc = `${url}#filename=${encodeURIComponent(resolvedName)}`;

  if (version === 'v1') {
    revokePdfUrl(pdfUrl.value);
    pdfBlob.value = blob;
    pdfFileName.value = resolvedName;
    pdfUrl.value = pdfSrc;
    return;
  }

  revokePdfUrl(pdfUrlV2.value);
  pdfBlobV2.value = blob;
  pdfFileNameV2.value = resolvedName;
  pdfUrlV2.value = pdfSrc;
};

const loadReportV2Pdf = async () => {
  if (!ccCode.value) return;

  try {
    const res = await crawlApi.getWageDiagnosisReportV2(ccCode.value);
    const base64 = res.data?.pdf;
    if (!base64) {
      throw new Error(res.data?.message || '보고서(v2) 데이터가 없습니다.');
    }
    applyPdfBase64(base64, res.data?.fileName, 'v2');
  } catch (e: any) {
    const msg = e.response?.data?.message || e.message || '알 수 없는 오류';
    const status = e.response?.status || '';
    error.value = `보고서(v2) 미리보기 실패: ${msg}${status ? ` (${status})` : ''}`;
    console.error('보고서 v2 PDF 조회 오류:', e);
  }
};

const initFromFacility = async () => {
  const f = props.facility;
  if (!f) return;

  ccCode.value = f.ccNumber || '';
  name.value = f.name || '';
  biz.value = f.biz || '';
  savedInfo.value = {
    ccNumber: f.ccNumber,
    name: f.name,
    biz: f.biz,
    wageDiagCount: f.wageDiagCount,
    wageDiagWdate: f.wageDiagWdate,
    wageDiagUId: f.wageDiagUId,
  };

  if (f.wageDiagCount && f.wageDiagCount > 0) {
    try {
      const res = await crawlApi.getWageDiagnosisActive(f.ccNumber);
      const meta = res.data?.data?.meta;
      if (meta?.cwd_ym) ym.value = meta.cwd_ym;
      if (meta?.cwd_non_tax_lunch != null) {
        nonTaxLunch.value = meta.cwd_non_tax_lunch === '1';
      }
    } catch (e) {
      console.error('임금진단 메타 조회 오류:', e);
    }

    await loadSavedReport();
  }
};

const close = () => {
  open.value = false;
};

const pickFile = () => {
  if (!ccCode.value) {
    error.value = '시설코드가 없습니다.';
    return;
  }
  fileRef.value?.click();
};

const parseExcel = (data: ArrayBuffer) => {
  const wb = XLSX.read(data, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json(sheet, {
    header: [
      'no', 'name', 'worker', 'time', 'base', 'overtime', 'night', 'holiday',
      'longTerm', 'dang', 'position', 'etc', 'lunch', 'car', 'childcare',
      'l_yeon', 'gun', 'jang', 'go', 'boss',
    ],
    range: 2,
  }) as Record<string, unknown>[];

  return json
    .filter((r) => r.name && r.worker)
    .map((r) => ({
      name: String(r.name || ''),
      worker: String(r.worker || '기타'),
      time: num(r.time),
      base: num(r.base),
      overtime: num(r.overtime),
      night: num(r.night),
      holiday: num(r.holiday),
      longTerm: num(r.longTerm),
      sunim: 0,
      dang: num(r.dang),
      position: num(r.position),
      etc: num(r.etc),
      lunch: num(r.lunch),
      car: num(r.car),
      childcare: num(r.childcare),
      l_yeon: num(r.l_yeon),
      gun: num(r.gun),
      jang: num(r.jang),
      go: num(r.go),
      boss: !!r.boss,
    }));
};

const uploadAndReport = async () => {
  error.value = '';

  if (!ccCode.value.trim()) {
    error.value = '시설코드가 필요합니다.';
    return;
  }
  if (!name.value.trim()) {
    error.value = '시설명을 입력해주세요.';
    return;
  }
  if (!ym.value) {
    error.value = '연월을 선택해주세요.';
    return;
  }
  if (rows.value.length === 0) {
    error.value = '엑셀 파일을 업로드해주세요.';
    return;
  }

  loading.value = true;
  loadingText.value = '저장 및 보고서 생성 중...';
  resetPdf();

  try {
    const res = await crawlApi.uploadWageDiagnosis({
      ccNumber: ccCode.value.trim(),
      name: name.value.trim(),
      biz: biz.value.trim(),
      ym: ym.value,
      nonTaxLunch: nonTaxLunch.value,
      rows: rows.value,
    });

    const base64 = res.data?.pdf;
    if (!base64) {
      throw new Error(res.data?.message || '보고서 데이터가 없습니다.');
    }

    applyPdfBase64(base64, res.data?.fileName, 'v1');
    await loadReportV2Pdf();
    savedInfo.value = {
      ccNumber: ccCode.value,
      name: name.value,
      biz: biz.value,
      wageDiagCount: res.data?.saved || rows.value.length,
      wageDiagWdate: new Date().toISOString(),
      wageDiagUId: res.data?.uploadId,
    };
    emit('saved', ccCode.value);
  } catch (e: any) {
    const msg = e.response?.data?.message || e.message || '알 수 없는 오류';
    const status = e.response?.status || '';
    error.value = `임금진단 실패: ${msg}${status ? ` (${status})` : ''}`;
    console.error('임금진단 업로드 오류:', e);
  } finally {
    loading.value = false;
  }
};

const reloadSavedReport = async (text = '보고서 미리보기 불러오는 중...') => {
  if (!ccCode.value) return;

  loading.value = true;
  loadingText.value = text;
  error.value = '';
  resetPdf();

  try {
    const res = await crawlApi.getWageDiagnosisReport(ccCode.value);
    const base64 = res.data?.pdf;
    if (!base64) {
      throw new Error(res.data?.message || '보고서 데이터가 없습니다.');
    }
    applyPdfBase64(base64, res.data?.fileName, 'v1');
    await loadReportV2Pdf();
  } catch (e: any) {
    const msg = e.response?.data?.message || e.message || '알 수 없는 오류';
    const status = e.response?.status || '';
    error.value = `보고서 미리보기 실패: ${msg}${status ? ` (${status})` : ''}`;
    console.error('저장 보고서 조회 오류:', e);
  } finally {
    loading.value = false;
  }
};

const loadSavedReport = async () => {
  await reloadSavedReport();
};

const onNonTaxLunchChange = async () => {
  if (!ccCode.value || !hasSaved.value) return;

  loading.value = true;
  loadingText.value = '설정 저장 및 보고서 재생성 중...';
  error.value = '';

  try {
    const res = await crawlApi.updateWageDiagnosisSettings(ccCode.value.trim(), {
      nonTaxLunch: nonTaxLunch.value,
    });
    if (!res.data?.success) {
      throw new Error(res.data?.message || '설정 저장에 실패했습니다.');
    }
    await reloadSavedReport('보고서 재생성 중...');
  } catch (e: any) {
    const msg = e.response?.data?.message || e.message || '알 수 없는 오류';
    const status = e.response?.status || '';
    error.value = `비과세 중식대 설정 실패: ${msg}${status ? ` (${status})` : ''}`;
    console.error('비과세 중식대 설정 오류:', e);
    loading.value = false;
  }
};

const onFileChange = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  if (hasSaved.value && !confirm('기존 임금진단 데이터가 덮어쓰기 됩니다. 계속하시겠습니까?')) {
    if (fileRef.value) fileRef.value.value = '';
    return;
  }

  error.value = '';
  fileName.value = file.name;

  try {
    const buffer = await file.arrayBuffer();
    rows.value = parseExcel(buffer);
    if (rows.value.length === 0) {
      error.value = '엑셀에서 유효한 데이터를 찾을 수 없습니다.';
      return;
    }
    await uploadAndReport();
  } catch (e: any) {
    error.value = `엑셀 처리 실패: ${e.message || '파일을 읽을 수 없습니다.'}`;
    console.error('엑셀 파싱 오류:', e);
  } finally {
    if (fileRef.value) fileRef.value.value = '';
  }
};

const downloadTemplate = async () => {
  try {
    await crawlApi.downloadWageDiagnosisTemplate();
  } catch (e: any) {
    const msg = e.response?.data?.message || e.message || '다운로드 실패';
    alert(`엑셀 양식 다운로드 실패: ${msg}`);
    console.error('양식 다운로드 오류:', e);
  }
};

const downloadPdf = () => {
  const blob = activePdfBlob.value;
  if (!blob) return;
  const link = document.createElement('a');
  const url = window.URL.createObjectURL(blob);
  link.href = url;
  link.download = activePdfFileName.value
    || (reportVersion.value === 'v1' ? buildPdfFileName() : buildPdfFileNameV2());
  link.click();
  window.URL.revokeObjectURL(url);
};

const confirmDelete = async () => {
  if (!ccCode.value) {
    error.value = '시설코드가 없습니다.';
    showDeleteConfirm.value = false;
    return;
  }

  deleting.value = true;
  error.value = '';

  try {
    const res = await crawlApi.deleteWageDiagnosis(ccCode.value.trim());
    if (!res.data?.success) {
      throw new Error(res.data?.message || '삭제에 실패했습니다.');
    }

    resetPdf();
    reportVersion.value = 'v1';
    rows.value = [];
    fileName.value = '';
    savedInfo.value = {
      ccNumber: ccCode.value,
      name: name.value,
      biz: biz.value,
      wageDiagCount: 0,
    };
    showDeleteConfirm.value = false;
    emit('deleted', ccCode.value);
  } catch (e: any) {
    const msg = e.response?.data?.message || e.message || '알 수 없는 오류';
    const status = e.response?.status || '';
    error.value = `임금진단 삭제 실패: ${msg}${status ? ` (${status})` : ''}`;
    console.error('임금진단 삭제 오류:', e);
  } finally {
    deleting.value = false;
  }
};

watch(open, async (v) => {
  if (v) {
    error.value = '';
    if (!ym.value) initYm();
    await initFromFacility();
  } else {
    resetPdf();
    rows.value = [];
    fileName.value = '';
    error.value = '';
    ccCode.value = '';
    name.value = '';
    biz.value = '';
    savedInfo.value = null;
    reportVersion.value = 'v1';
    showDeleteConfirm.value = false;
    deleting.value = false;
  }
});

onUnmounted(() => {
  resetPdf();
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_v_dialog, {
      modelValue: open.value,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((open).value = $event)),
      "max-width": "1480",
      persistent: ""
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, { class: "wage-diag-dialog" }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("div", _hoisted_2, [
                _createVNode(_component_v_icon, {
                  size: "18",
                  color: "white"
                }, {
                  default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                    _createTextVNode("mdi-file-chart", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _createElementVNode("div", _hoisted_3, [
                _cache[11] || (_cache[11] = _createElementVNode("h3", { class: "wage-diag-header__title" }, "임금진단", -1)),
                (ccCode.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(name.value) + " · " + _toDisplayString(ccCode.value), 1))
                  : _createCommentVNode("", true)
              ]),
              _createVNode(_component_v_spacer),
              _createVNode(_component_v_btn, {
                variant: "text",
                class: "btn-close",
                onClick: close
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, null, {
                    default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                      _createTextVNode("mdi-close", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _createVNode(_component_v_card_text, { class: "wage-diag-body" }, {
              default: _withCtx(() => [
                _createVNode(LoadingOverlay, {
                  show: loading.value || deleting.value,
                  text: deleting.value ? '삭제 중...' : loadingText.value,
                  variant: deleting.value ? 'delete' : 'default'
                }, null, 8, ["show", "text", "variant"]),
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_component_v_text_field, {
                    modelValue: ccCode.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((ccCode).value = $event)),
                    label: "시설코드",
                    density: "compact",
                    variant: "outlined",
                    "hide-details": "",
                    readonly: "",
                    class: "wage-diag-field wage-diag-field--code"
                  }, null, 8, ["modelValue"]),
                  _createVNode(_component_v_text_field, {
                    modelValue: name.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((name).value = $event)),
                    label: "시설명",
                    density: "compact",
                    variant: "outlined",
                    "hide-details": "",
                    class: "wage-diag-field"
                  }, null, 8, ["modelValue"]),
                  _createVNode(_component_v_text_field, {
                    modelValue: biz.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((biz).value = $event)),
                    label: "사업구분",
                    density: "compact",
                    variant: "outlined",
                    "hide-details": "",
                    class: "wage-diag-field"
                  }, null, 8, ["modelValue"]),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((ym).value = $event)),
                    type: "month",
                    class: "wage-diag-month",
                    min: "2020-01",
                    max: "2050-12"
                  }, null, 512), [
                    [_vModelText, ym.value]
                  ]),
                  _createElementVNode("label", _hoisted_6, [
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((nonTaxLunch).value = $event)),
                      type: "checkbox",
                      disabled: loading.value || deleting.value,
                      onChange: onNonTaxLunchChange
                    }, null, 40, _hoisted_7), [
                      [_vModelCheckbox, nonTaxLunch.value]
                    ]),
                    _cache[13] || (_cache[13] = _createElementVNode("span", null, "비과세 중식대 적용", -1))
                  ])
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createVNode(_component_v_btn, {
                    variant: "flat",
                    class: "btn-reset",
                    onClick: downloadTemplate
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, {
                        size: "16",
                        class: "mr-1"
                      }, {
                        default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                          _createTextVNode("mdi-download", -1)
                        ]))]),
                        _: 1
                      }),
                      _cache[15] || (_cache[15] = _createTextVNode(" 엑셀 양식 ", -1))
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    variant: "flat",
                    class: "btn-register",
                    onClick: pickFile
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, {
                        size: "16",
                        class: "mr-1"
                      }, {
                        default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                          _createTextVNode("mdi-file-excel", -1)
                        ]))]),
                        _: 1
                      }),
                      _cache[17] || (_cache[17] = _createTextVNode(" 엑셀 업로드 ", -1))
                    ]),
                    _: 1
                  }),
                  (hasSaved.value)
                    ? (_openBlock(), _createBlock(_component_v_btn, {
                        key: 0,
                        variant: "flat",
                        class: "btn-delete",
                        disabled: loading.value || deleting.value,
                        onClick: _cache[5] || (_cache[5] = ($event: any) => (showDeleteConfirm.value = true))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_icon, {
                            size: "16",
                            class: "mr-1"
                          }, {
                            default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                              _createTextVNode("mdi-delete-outline", -1)
                            ]))]),
                            _: 1
                          }),
                          _cache[19] || (_cache[19] = _createTextVNode(" 진단 삭제 ", -1))
                        ]),
                        _: 1
                      }, 8, ["disabled"]))
                    : _createCommentVNode("", true),
                  _createElementVNode("input", {
                    ref_key: "fileRef",
                    ref: fileRef,
                    type: "file",
                    accept: ".xlsx,.xls",
                    class: "wage-diag-file-hidden",
                    onChange: onFileChange
                  }, null, 544),
                  (fileName.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(fileName.value) + " (" + _toDisplayString(rowCount.value) + "건)", 1))
                    : _createCommentVNode("", true)
                ]),
                (error.value)
                  ? (_openBlock(), _createBlock(_component_v_alert, {
                      key: 0,
                      type: "error",
                      density: "compact",
                      class: "mt-3"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(error.value), 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("div", _hoisted_11, [
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("button", {
                        type: "button",
                        class: _normalizeClass(["wage-diag-preview__tab", { 'wage-diag-preview__tab--active': reportVersion.value === 'v1' }]),
                        onClick: _cache[6] || (_cache[6] = ($event: any) => (reportVersion.value = 'v1'))
                      }, " 버전1 ", 2),
                      _createElementVNode("button", {
                        type: "button",
                        class: _normalizeClass(["wage-diag-preview__tab", { 'wage-diag-preview__tab--active': reportVersion.value === 'v2' }]),
                        onClick: _cache[7] || (_cache[7] = ($event: any) => (reportVersion.value = 'v2'))
                      }, " 버전2 ", 2)
                    ]),
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("span", _hoisted_14, _toDisplayString(reportVersion.value === 'v1' ? '임금대장 개선 결과보고서' : '임금대장 무료 진단 보고서'), 1),
                      (activePdfFileName.value)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString(activePdfFileName.value), 1))
                        : _createCommentVNode("", true)
                    ]),
                    _createElementVNode("div", _hoisted_16, [
                      (reportVersion.value === 'v2' && isSuperAdmin.value)
                        ? (_openBlock(), _createBlock(_component_v_menu, {
                            key: 0,
                            location: "bottom end",
                            "close-on-content-click": false,
                            "max-width": "440"
                          }, {
                            activator: _withCtx(({ props: menuProps }) => [
                              _createVNode(_component_v_btn, _mergeProps(menuProps, {
                                variant: "text",
                                icon: "",
                                size: "small",
                                class: "wage-diag-formula-help-btn",
                                title: "통상임금 리스크 계산식 (최고관리자)"
                              }), {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_icon, {
                                    size: "18",
                                    color: "#546e7a"
                                  }, {
                                    default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                                      _createTextVNode("mdi-help-circle-outline", -1)
                                    ]))]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }, 16)
                            ]),
                            default: _withCtx(() => [
                              _createVNode(_component_v_card, { class: "wage-diag-formula-card" }, {
                                default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                                  _createElementVNode("div", { class: "wage-diag-formula-card__title" }, "통상임금 리스크 계산식 (버전2)", -1),
                                  _createElementVNode("div", { class: "wage-diag-formula-card__badge" }, "최고관리자 전용", -1),
                                  _createElementVNode("div", { class: "wage-diag-formula-card__section" }, [
                                    _createElementVNode("div", { class: "wage-diag-formula-card__label" }, "판결 이전 통상임금 (BEFORE)"),
                                    _createElementVNode("div", { class: "wage-diag-formula-card__formula" }, "= 기본급 (업로드 원본)")
                                  ], -1),
                                  _createElementVNode("div", { class: "wage-diag-formula-card__section" }, [
                                    _createElementVNode("div", { class: "wage-diag-formula-card__label" }, "판결 이후 통상임금 (AFTER)"),
                                    _createElementVNode("div", { class: "wage-diag-formula-card__formula" }, "= 기본급 + 중식대(▲) + 직책수당(▲)"),
                                    _createElementVNode("ul", { class: "wage-diag-formula-card__notes" }, [
                                      _createElementVNode("li", null, "중식대(▲): 비과세 중식대 처리 후 실제 지급액 (예: 20만)"),
                                      _createElementVNode("li", null, "직책수당(▲): 엑셀 직책수당 컬럼"),
                                      _createElementVNode("li", null, "기타수당: 통상임금·비과세와 별도 과세 수당"),
                                      _createElementVNode("li", null, "업로드 원본 중식대(예: 6만)와 처리 후 금액이 다를 수 있음")
                                    ])
                                  ], -1),
                                  _createElementVNode("div", { class: "wage-diag-formula-card__section" }, [
                                    _createElementVNode("div", { class: "wage-diag-formula-card__label" }, "통상시급 상승률"),
                                    _createElementVNode("div", { class: "wage-diag-formula-card__formula" }, "= AFTER ÷ BEFORE")
                                  ], -1),
                                  _createElementVNode("div", { class: "wage-diag-formula-card__section" }, [
                                    _createElementVNode("div", { class: "wage-diag-formula-card__label" }, "월 리스크 (RISK A, 1인)"),
                                    _createElementVNode("div", { class: "wage-diag-formula-card__formula" }, "= (연장 + 야간 + 휴일) × (상승률 − 1)")
                                  ], -1),
                                  _createElementVNode("div", { class: "wage-diag-formula-card__section" }, [
                                    _createElementVNode("div", { class: "wage-diag-formula-card__label" }, "3년 누적 리스크 (1인)"),
                                    _createElementVNode("div", { class: "wage-diag-formula-card__formula" }, "= 월 리스크 × 36개월")
                                  ], -1),
                                  _createElementVNode("div", { class: "wage-diag-formula-card__example" }, " 예) 기본 200만, 중식 20만, 직책 10만 → +15% → 연야 35만 × 15% = 52,500원/월 ", -1)
                                ]))]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true),
                      _createVNode(_component_v_btn, {
                        variant: "flat",
                        class: "btn-save",
                        size: "small",
                        disabled: !activePdfBlob.value,
                        onClick: downloadPdf
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_icon, {
                            size: "16",
                            class: "mr-1"
                          }, {
                            default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                              _createTextVNode("mdi-download", -1)
                            ]))]),
                            _: 1
                          }),
                          _cache[23] || (_cache[23] = _createTextVNode(" PDF 다운로드 ", -1))
                        ]),
                        _: 1
                      }, 8, ["disabled"])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_17, [
                    (activePdfUrl.value)
                      ? (_openBlock(), _createElementBlock("iframe", {
                          key: 0,
                          src: activePdfUrl.value,
                          title: activePdfFileName.value || '임금간편진단 보고서',
                          class: "wage-diag-preview__frame"
                        }, null, 8, _hoisted_18))
                      : (hasReportPreview.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                            _createVNode(_component_v_icon, {
                              size: "40",
                              color: "#90a4ae"
                            }, {
                              default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                                _createTextVNode("mdi-file-pdf-box", -1)
                              ]))]),
                              _: 1
                            }),
                            _createElementVNode("p", null, _toDisplayString(reportVersion.value === 'v1' ? '버전1' : '버전2') + " 보고서를 불러오지 못했습니다.", 1)
                          ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_20, [
                            _createVNode(_component_v_icon, {
                              size: "40",
                              color: "#90a4ae"
                            }, {
                              default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                                _createTextVNode("mdi-file-pdf-box", -1)
                              ]))]),
                              _: 1
                            }),
                            _cache[26] || (_cache[26] = _createElementVNode("p", null, "엑셀 업로드 후 보고서가 미리보기로 표시됩니다.", -1))
                          ]))
                  ])
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(DeleteConfirmDialog, {
      modelValue: showDeleteConfirm.value,
      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((showDeleteConfirm).value = $event)),
      title: "임금진단 삭제",
      "target-name": name.value || ccCode.value,
      message: "의 임금진단 데이터를 삭제하시겠습니까?",
      loading: deleting.value,
      onConfirm: confirmDelete
    }, null, 8, ["modelValue", "target-name", "loading"])
  ], 64))
}
}

})